import { IPermissions } from '@models/Configuration/IPermissions';
import { ConfigService } from '@services/config.service';
import _ from 'lodash';

// ATTENTION: Modifications to this file have to be always be made on BACKEND and FRONTEND!

export function getPermissions(appConfig: ConfigService): IPermissions {
  return {
    chat: {
      message: {
        post: function (channel, message) {
          return !!_.find(channel.participants, message.created_by);
        },
      },
      post: function () {
        return true;
      },
    },
    session: {
      follow: function (session) {
        return !!session.me;
      },
      unfollow: function (session) {
        return !!session.me;
      },
      get: function (session) {
        return !!session.me;
      },
      delete: function (session) {
        return session.me && session.me.role <= appConfig.roles.SESSION_ADMIN;
      },
      export: function (session) {
        return (
          session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
        );
      },
      import: function (session) {
        return (
          session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
        );
      },
      data: {
        ideas: {
          maturity: function (session, idea) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === idea.created_by)
            );
          },
          delete: function (session, idea) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === idea.created_by)
            );
          },
          put: function (session, idea) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === idea.created_by)
            );
          },
          unpublish: function (session, idea) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === idea.created_by) &&
              idea.published
            );
          },
          publish: function (session, idea) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === idea.created_by) &&
              !idea.published
            );
          },
          position: function (session, idea) {
            return session.me && session.me._id.toString() === idea.created_by;
          },
          post: function (session) {
            return (
              session.me &&
              session.me.role <= appConfig.roles.SESSION_PARTICIPANT
            );
          },
          get: function (session, idea) {
            return (
              session.me &&
              (idea.published ||
                idea.created_by === session.me._id.toString() ||
                idea.collaborators
                  .map(collaborator => collaborator.participant_id.toString())
                  .includes(session.me._id.toString()) ||
                (idea.collaboratorInvites || [])
                  .map(invite => invite.participant_id.toString())
                  .includes(session.me._id.toString())) &&
              !idea.deleted_at
            );
          },
          follow: function (session) {
            return !!session.me;
          },
          unfollow: function (session) {
            return !!session.me;
          },
          vote: function (session, idea) {
            const creator: any = _.find(session.config.participants, {
              _id: idea.created_by,
            });
            return (
              idea.published &&
              ((idea.created_by === session.me._id.toString() &&
                session.config.vote_own_ideas) ||
                (creator &&
                  session.me.votes_for &&
                  session.me.votes_for.indexOf(creator.team) !== -1))
            );
          },
          benefits: {
            delete: function (session, benefit) {
              return (
                session.me &&
                (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                  session.me._id.toString() === benefit.created_by)
              );
            },
            post: function (session) {
              return (
                session.me &&
                session.me.role <= appConfig.roles.SESSION_PARTICIPANT
              );
            },
          },
          challenges: {
            delete: function (session, challenge) {
              return (
                session.me &&
                (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                  session.me._id.toString() === challenge.created_by)
              );
            },
            post: function (session) {
              return (
                session.me &&
                session.me.role <= appConfig.roles.SESSION_PARTICIPANT
              );
            },
            solutions: {
              delete: function (session, solution) {
                return (
                  session.me &&
                  (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                    session.me._id.toString() === solution.created_by)
                );
              },
              post: function (session) {
                return (
                  session.me &&
                  session.me.role <= appConfig.roles.SESSION_PARTICIPANT
                );
              },
              accept: function (session, challenge) {
                return (
                  session.me &&
                  (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                    session.me._id.toString() === challenge.created_by)
                );
              },
              unaccept: function (session, challenge, solution) {
                return (
                  session.me &&
                  (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                    session.me._id.toString() === challenge.created_by ||
                    session.me._id.toString() === solution.created_by)
                );
              },
            },
          },
          comments: {
            delete: function (session, comment) {
              return (
                session.me &&
                (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                  session.me._id.toString() === comment.created_by)
              );
            },
            post: function (session) {
              return (
                session.me &&
                session.me.role <= appConfig.roles.SESSION_EXTERNAL_EVALUATOR
              );
            },
          },
          collaborators: {
            delete: function (session, collaborator, idea) {
              return (
                session.me &&
                (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                  (collaborator &&
                    session.me._id.toString() ===
                      collaborator.participant_id) ||
                  session.me._id.toString() === idea.created_by)
              );
            },
            post: function (session, idea) {
              return (
                (session.me && // logged in?
                  (session.me.role <= appConfig.roles.SESSION_MODERATOR || // moderator or better?
                    session.me._id.toString() === idea.created_by)) || // my own idea?
                idea.collaborators
                  .map(c => c.participant_id)
                  .includes(session.me._id)
              ); // am I collaborator on this idea?
            },
          },
        },
        announcements: {
          post: function (session) {
            return (
              session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
            );
          },
          read: function (session) {
            return !!session.me;
          },
        },
        morphboxes: {
          delete: function (session, morphbox) {
            return (
              session.me &&
              morphbox.rows.length === 0 &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                session.me._id.toString() === morphbox.created_by)
            );
          },
          post: function (session) {
            return (
              session.me &&
              session.me.role <= appConfig.roles.SESSION_PARTICIPANT
            );
          },
          rows: {
            delete: function (session, morphbox, row) {
              return (
                session.me &&
                morphbox.editable &&
                row.elements.length === 0 &&
                (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                  session.me._id.toString() === row.created_by)
              );
            },
            post: function (session, morphbox) {
              return (
                session.me &&
                morphbox.editable &&
                session.me.role <= appConfig.roles.SESSION_PARTICIPANT
              );
            },
            elements: {
              delete: function (session, row, element) {
                return (
                  session.me &&
                  row.editable &&
                  (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                    session.me._id.toString() === element.created_by)
                );
              },
              post: function (session, row) {
                return (
                  session.me &&
                  row.editable &&
                  session.me.role <= appConfig.roles.SESSION_PARTICIPANT
                );
              },
            },
          },
        },
        notifications: {
          read: function (session) {
            return !!session.me;
          },
        },
      },
      config: {
        edit: function (session) {
          return (
            session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
          );
        },
        admin: function (session) {
          return session.me && session.me.role <= appConfig.roles.SESSION_ADMIN;
        },
        participants: {
          add: function (session) {
            return (
              session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
            );
          },
          delete: function (session, participant) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                participant._id.toString() === session.me._id.toString())
            );
          },
          put: function (session) {
            return (
              session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
            );
          },
          privacy: {
            put: function (session) {
              return !!session.me;
            },
          },
        },
        teams: {
          add: function (session) {
            return (
              session.me && session.me.role <= appConfig.roles.SESSION_MODERATOR
            );
          },
          delete: function (session, team) {
            return (
              session.me &&
              (session.me.role <= appConfig.roles.SESSION_MODERATOR ||
                team._id.toString() === session.me._id.toString())
            );
          },
        },
      },
    },
  };
}
